import Phaser from 'phaser';
import type { FactionId } from '../../shared/types';

/**
 * meta-base-art.ts — 「指揮官基地」向量繪圖庫(meta-shop 視覺技能樹的呈現核心).
 *
 * 設計(boss 2026-06-15「永久升級商店做成 Rogue Legacy 式『基地會長大』的輕量視覺
 * 技能樹,而且要畫好看」):
 *   - 5 門派各一座**主題化基地**,買升級節點 = 基地上**長出/點亮一個新部件**。
 *   - 未買部件 = 半透明剪影(暗、灰);已買 = 亮起 + 發光 + 等級小圓點(Lv x/max)。
 *   - 純 Phaser Graphics 向量(無美術素材):形狀 + 漸層 + 發光 + emoji 點綴。
 *   - 受眾國小三年級(9 歲)→ 部件大、輪廓清楚、顏色飽和、發光明確。
 *
 * 雙軌進度鐵則:本檔**純呈現**,不讀寫任何 progress 欄位、不碰數值/經濟/天花板。
 *   它只接受 caller 傳入的「每個 node 目前等級 / max」,回畫 GameObject。
 *
 * 與 meta-shop.ts 的關係:每座基地的 part 用 `nodeId` 對齊 META_HERO_TREES 的節點 id。
 *   caller(meta-shop-scene)把 part 的命中區交給 Phaser,點 part → 開該 node 的資訊卡。
 */

// ── 門派主題色板(每門派一座基地,色調對齊 boss 視覺概念)─────────────────────
//
// boss 指定:
//   ⚡ 星算門(觀星台/水晶塔)— 紫羅蘭
//   🔮 符文門(符文神殿/盾牆)— 靛藍
//   🤖 機械門(機械車庫/齒輪廠)— 青 teal
//   🌀 自然門(生態圓頂/樹屋)— 翠綠
//   🏰 疆域門(城堡/要塞)— 琥珀金
export interface BasePalette {
  /** 主結構色(亮起時的部件主色)。 */
  primary: number;
  /** 次色(漸層較亮端 / 高光)。 */
  light: number;
  /** 深色(漸層較暗端 / 陰影、地基)。 */
  dark: number;
  /** 發光暈色(已買部件的光暈)。 */
  glow: number;
  /** 天幕上半色(基地背景漸層頂)。 */
  skyTop: number;
  /** 天幕下半色(基地背景漸層底,接地基)。 */
  skyBottom: number;
  /** 基地名稱(主視覺副標)。 */
  name: string;
  /** 門派 emoji。 */
  emoji: string;
}

export const BASE_PALETTES: Readonly<Record<FactionId, BasePalette>> = {
  // ⚡ 星算門 — 觀星台 / 水晶塔(紫羅蘭)
  celestial: {
    primary: 0x9c6bff, light: 0xd2b3ff, dark: 0x3a1f6e, glow: 0xb98bff,
    skyTop: 0x1a0f3d, skyBottom: 0x2a1858, name: '觀星水晶塔', emoji: '⚡',
  },
  // 🔮 符文門 — 符文神殿 / 盾牆(靛藍)
  runic: {
    primary: 0x5c7cff, light: 0xa9bcff, dark: 0x1f2a66, glow: 0x7d97ff,
    skyTop: 0x0f1438, skyBottom: 0x182052, name: '符文神殿', emoji: '🔮',
  },
  // 🤖 機械門 — 機械車庫 / 齒輪廠(青 teal)
  techno: {
    primary: 0x2fd6c6, light: 0x9af0e8, dark: 0x0d4f4a, glow: 0x4ce6d6,
    skyTop: 0x07232a, skyBottom: 0x0c3640, name: '機械齒輪廠', emoji: '🤖',
  },
  // 🌀 自然門 — 生態圓頂 / 樹屋(翠綠)
  elemental: {
    primary: 0x46c46a, light: 0xa6f0b8, dark: 0x16502b, glow: 0x6fe08c,
    skyTop: 0x0a2417, skyBottom: 0x123a23, name: '生態樹屋圓頂', emoji: '🌀',
  },
  // 🏰 疆域門 — 城堡 / 要塞(琥珀金)
  dominion: {
    primary: 0xf5b53d, light: 0xffe09a, dark: 0x6e4310, glow: 0xffca5e,
    skyTop: 0x241704, skyBottom: 0x3a2708, name: '黃金要塞', emoji: '🏰',
  },
};

/** caller 傳入的「某部件目前狀態」:level / max。 */
export interface PartState {
  level: number;
  maxLevel: number;
}

/** 一個基地部件:對齊一個 meta-shop node。包含命中圓資訊。 */
export interface BasePart {
  /** 對齊 META_HERO_TREES 的 node id。 */
  nodeId: string;
  /** 部件視覺中心(基地局部座標)。 */
  cx: number;
  cy: number;
  /** 命中半徑(點選用,9 歲手指友善,通常比視覺略大)。 */
  hitRadius: number;
}

// ── 幾何配置:每座基地的部件擺位(基地局部座標,原點在 base canvas 左上)─────────
//
// 基地畫布尺寸統一 BASE_W × BASE_H,部件用相對座標擺放,container 再置中。
// 部件順序對齊 META_HERO_TREES.nodes(4 節點/門派),前置鏈:node[0] 是核心,
// 其餘掛在核心周邊(用發光連線連回核心,1 層前置,K-3 友善)。

export const BASE_W = 360;
export const BASE_H = 380;

/**
 * 取某門派的部件擺位(node 順序對齊 META_HERO_TREES.nodes)。
 * 每門派 4 個部件:[0]=核心(中央偏下,基地主體)、[1..3]=周邊(塔/翼/裝置)。
 * 擺位刻意做出「一座建築」的剪影感,而非散落圓點。
 */
export function partLayoutFor(faction: FactionId): Array<{ cx: number; cy: number; hitRadius: number }> {
  const cxMid = BASE_W / 2;
  switch (faction) {
    case 'celestial': // 觀星水晶塔:中央高塔(核心)+ 兩側水晶 + 頂端天文台
      return [
        { cx: cxMid,       cy: 232, hitRadius: 56 }, // 核心:塔身
        { cx: cxMid,       cy: 96,  hitRadius: 48 }, // 頂:天文台圓頂
        { cx: cxMid - 104, cy: 286, hitRadius: 42 }, // 左:水晶
        { cx: cxMid + 104, cy: 286, hitRadius: 42 }, // 右:水晶
      ];
    case 'runic': // 符文神殿:中央神殿(核心)+ 頂部符文 + 左右盾牆
      return [
        { cx: cxMid,       cy: 226, hitRadius: 58 }, // 核心:神殿主體
        { cx: cxMid,       cy: 104, hitRadius: 46 }, // 頂:符文核心
        { cx: cxMid - 112, cy: 270, hitRadius: 42 }, // 左:盾牆
        { cx: cxMid + 112, cy: 270, hitRadius: 42 }, // 右:盾牆
      ];
    case 'techno': // 機械齒輪廠:中央車庫(核心)+ 大齒輪 + 推進器 + 雷達天線
      return [
        { cx: cxMid,       cy: 238, hitRadius: 60 }, // 核心:車庫主體
        { cx: cxMid + 92,  cy: 120, hitRadius: 46 }, // 右上:大齒輪
        { cx: cxMid - 110, cy: 250, hitRadius: 42 }, // 左:推進器
        { cx: cxMid - 92,  cy: 122, hitRadius: 40 }, // 左上:雷達天線
      ];
    case 'elemental': // 生態樹屋圓頂:中央巨樹(核心)+ 圓頂 + 左右花叢
      return [
        { cx: cxMid,       cy: 234, hitRadius: 58 }, // 核心:巨樹樹幹/樹冠
        { cx: cxMid,       cy: 110, hitRadius: 50 }, // 頂:生態圓頂
        { cx: cxMid - 108, cy: 292, hitRadius: 40 }, // 左:花叢
        { cx: cxMid + 108, cy: 292, hitRadius: 40 }, // 右:花叢
      ];
    case 'dominion': // 黃金要塞:中央主堡(核心)+ 旗塔 + 左右角樓
    default:
      return [
        { cx: cxMid,       cy: 232, hitRadius: 60 }, // 核心:主堡
        { cx: cxMid,       cy: 96,  hitRadius: 46 }, // 頂:旗塔
        { cx: cxMid - 116, cy: 268, hitRadius: 44 }, // 左:角樓
        { cx: cxMid + 116, cy: 268, hitRadius: 44 }, // 右:角樓
      ];
  }
}

/** 一個 lerp 工具(0..1)。 */
function lerpColor(a: number, b: number, t: number): number {
  const ca = Phaser.Display.Color.ValueToColor(a);
  const cb = Phaser.Display.Color.ValueToColor(b);
  const c = Phaser.Display.Color.Interpolate.ColorWithColor(ca, cb, 100, Phaser.Math.Clamp(t, 0, 1) * 100);
  return Phaser.Display.Color.GetColor(c.r, c.g, c.b);
}

/**
 * 在 graphics 上畫「縱向漸層矩形」(無真 gradient API → 堆疊細橫條)。
 * 用於基地天幕背景。
 */
export function fillVerticalGradient(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  top: number, bottom: number, steps = 40,
): void {
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    g.fillStyle(lerpColor(top, bottom, t), 1);
    g.fillRect(x, y + (h / steps) * i, w, h / steps + 1);
  }
}

/**
 * 畫一座基地天幕背景(漸層 + 地基平台)到 graphics。
 * 部件由 drawPart 另外畫(疊在天幕之上)。faction 決定色板。
 */
export function drawBaseBackdrop(
  g: Phaser.GameObjects.Graphics,
  faction: FactionId,
): void {
  const pal = BASE_PALETTES[faction];
  // 天幕漸層
  fillVerticalGradient(g, 0, 0, BASE_W, BASE_H, pal.skyTop, pal.skyBottom, 48);

  // 地基平台(基地坐落的台座):圓角矩形 + 高光帶
  const baseY = BASE_H - 44;
  g.fillStyle(lerpColor(pal.dark, 0x000000, 0.35), 1);
  g.fillRoundedRect(28, baseY, BASE_W - 56, 60, 14);
  g.fillStyle(lerpColor(pal.dark, pal.primary, 0.25), 1);
  g.fillRoundedRect(36, baseY, BASE_W - 72, 16, { tl: 12, tr: 12, bl: 0, br: 0 });
  // 台座發光細邊
  g.lineStyle(2, pal.glow, 0.35);
  g.strokeRoundedRect(28, baseY, BASE_W - 56, 60, 14);
}

/**
 * 在天幕上加幾顆靜態星點(裝飾;deterministic 避免每次抖動)。
 * caller 傳 scene 以便建立 circle GameObject(加進 container)。
 */
export function addBackdropStars(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  faction: FactionId,
): void {
  const pal = BASE_PALETTES[faction];
  const rnd = new Phaser.Math.RandomDataGenerator([`base-${faction}`]);
  for (let i = 0; i < 22; i++) {
    const x = rnd.between(10, BASE_W - 10);
    const y = rnd.between(8, Math.floor(BASE_H * 0.55));
    const r = rnd.realInRange(0.6, 1.8);
    const star = scene.add.circle(x, y, r, lerpColor(pal.light, 0xffffff, 0.5), rnd.realInRange(0.3, 0.9));
    container.add(star);
    if (rnd.frac() < 0.4) {
      scene.tweens.add({
        targets: star, alpha: 0.12, duration: rnd.between(1100, 2400),
        yoyo: true, repeat: -1, delay: rnd.between(0, 1600), ease: 'Sine.easeInOut',
      });
    }
  }
}

/** drawPart 的輸入:部件 index(0=核心)、狀態(等級)、是否鎖住 / 可負擔。 */
export interface DrawPartOpts {
  faction: FactionId;
  /** 部件在 partLayoutFor 的 index(決定畫哪種形狀)。 */
  partIndex: number;
  state: PartState;
  /** 是否被前置鏈鎖住(核心未買時周邊鎖住)。鎖住 → 更暗、加鎖頭。 */
  locked: boolean;
  /** 是否買得起下一級(未滿且可負擔)→ 加「可升」呼吸光,吸引點擊。 */
  canAfford: boolean;
}

/**
 * 畫一個基地部件到一個**獨立 container**(回傳),caller 定位到 part.cx/part.cy。
 *
 * 視覺狀態三段:
 *   - locked(前置未解)→ 深灰剪影 + 🔒,低透明。
 *   - unbought(可買、level 0)→ 半透明門派色剪影(「待建」),canAfford 時加微光呼吸。
 *   - bought(level ≥ 1)→ 實心門派色 + 漸層高光 + 外圈發光 + Lv 圓點;
 *     越高級越亮(level/max 比例驅動光度與圓點數)。
 *
 * 形狀依 faction + partIndex 分流(每門派 4 種造型),做出「一座建築」的局部感。
 * 純呈現,不碰 progress。
 */
export function drawPart(
  scene: Phaser.Scene,
  opts: DrawPartOpts,
): Phaser.GameObjects.Container {
  const { faction, partIndex, state, locked } = opts;
  const pal = BASE_PALETTES[faction];
  const c = scene.add.container(0, 0);
  const bought = state.level >= 1;
  const ratio = state.maxLevel > 0 ? state.level / state.maxLevel : 0;

  // 外圈發光(已買才有;強度隨 level 比例)。
  if (bought) {
    const halo = scene.add.graphics();
    const haloR = 40 + ratio * 16;
    halo.fillStyle(pal.glow, 0.12 + ratio * 0.12);
    halo.fillCircle(0, 0, haloR + 10);
    halo.fillStyle(pal.glow, 0.18 + ratio * 0.16);
    halo.fillCircle(0, 0, haloR);
    c.add(halo);
    // 呼吸光暈(已買部件持續發亮,讓基地「活著」)
    scene.tweens.add({
      targets: halo, alpha: 0.55, scaleX: 1.08, scaleY: 1.08,
      duration: 1600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  } else if (opts.canAfford && !locked) {
    // 可買未買 → 柔和呼吸提示環(招手「來蓋我」)
    const hint = scene.add.graphics();
    hint.lineStyle(3, pal.glow, 0.5);
    hint.strokeCircle(0, 0, 42);
    c.add(hint);
    scene.tweens.add({
      targets: hint, alpha: 0.15, scaleX: 1.12, scaleY: 1.12,
      duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  // 部件主體(門派 + partIndex 分流造型)。
  const body = scene.add.graphics();
  drawPartShape(body, faction, partIndex, { bought, locked, ratio, pal });
  c.add(body);

  // 鎖頭(前置未解)。
  if (locked) {
    const lock = scene.add.text(0, 0, '🔒', { fontSize: '26px', fontFamily: 'sans-serif' }).setOrigin(0.5);
    c.add(lock);
  }

  // Lv 圓點(已買 → 顯示 level/max;未買不顯示)。置於部件下緣。
  if (bought) {
    c.add(buildLevelDots(scene, state, pal));
  }

  return c;
}

/** 已買部件的等級小圓點:亮點 = 已升級數,暗點 = 尚可升,滿級全亮 + 一顆星。 */
function buildLevelDots(
  scene: Phaser.Scene,
  state: PartState,
  pal: BasePalette,
): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);
  const n = Math.max(1, state.maxLevel);
  const gap = 13;
  const total = (n - 1) * gap;
  const y = 50;
  for (let i = 0; i < n; i++) {
    const x = -total / 2 + i * gap;
    const on = i < state.level;
    const dot = scene.add.graphics();
    if (on) {
      dot.fillStyle(pal.light, 1);
      dot.fillCircle(x, y, 5);
      dot.lineStyle(1.5, 0xffffff, 0.8);
      dot.strokeCircle(x, y, 5);
    } else {
      dot.fillStyle(0x000000, 0.45);
      dot.fillCircle(x, y, 4);
      dot.lineStyle(1.5, pal.primary, 0.5);
      dot.strokeCircle(x, y, 4);
    }
    c.add(dot);
  }
  // 滿級 → 加一顆星
  if (state.level >= state.maxLevel) {
    const star = scene.add.text(total / 2 + 16, y, '★', {
      fontSize: '15px', fontFamily: 'sans-serif', color: '#ffe9a8',
    }).setOrigin(0.5);
    c.add(star);
  }
  return c;
}

interface ShapeOpts {
  bought: boolean;
  locked: boolean;
  /** level / maxLevel(0..1),驅動亮度。 */
  ratio: number;
  pal: BasePalette;
}

/** 依狀態算「部件主色」與「描邊色 / 透明度」。 */
function shapeColors(o: ShapeOpts): Col {
  if (o.locked) {
    return { fill: 0x222838, fillAlpha: 0.7, stroke: 0x3a4256, strokeAlpha: 0.7, hi: 0x2c3346 };
  }
  if (!o.bought) {
    // 待建剪影:門派色但低透明 + 偏暗
    return {
      fill: lerpColor(o.pal.dark, 0x0a0a14, 0.2), fillAlpha: 0.55,
      stroke: o.pal.primary, strokeAlpha: 0.55, hi: o.pal.dark,
    };
  }
  // 已買:亮度隨 ratio 提升
  const fill = lerpColor(o.pal.dark, o.pal.primary, 0.55 + o.ratio * 0.45);
  const hi = lerpColor(o.pal.primary, o.pal.light, 0.4 + o.ratio * 0.6);
  return { fill, fillAlpha: 1, stroke: o.pal.light, strokeAlpha: 0.9, hi };
}

interface Col { fill: number; fillAlpha: number; stroke: number; strokeAlpha: number; hi: number }

/** 圓角矩形 + 頂部高光帶(已買部件的「上漆」質感)。 */
function panel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, r: number, col: Col): void {
  g.fillStyle(col.fill, col.fillAlpha);
  g.fillRoundedRect(x, y, w, h, r);
  // 頂部高光(漸層感)
  g.fillStyle(col.hi, col.fillAlpha * 0.55);
  g.fillRoundedRect(x, y, w, Math.max(6, h * 0.34), { tl: r, tr: r, bl: 0, br: 0 });
  g.lineStyle(2.5, col.stroke, col.strokeAlpha);
  g.strokeRoundedRect(x, y, w, h, r);
}

/**
 * 部件造型分流:每門派 4 個 partIndex 各畫不同形狀(對齊基地主題)。
 * 全部以部件中心 (0,0) 為原點繪製(caller container 已定位)。
 */
function drawPartShape(
  g: Phaser.GameObjects.Graphics,
  faction: FactionId,
  partIndex: number,
  o: ShapeOpts,
): void {
  const col = shapeColors(o);
  switch (faction) {
    case 'celestial': return drawCelestialPart(g, partIndex, col, o);
    case 'runic':     return drawRunicPart(g, partIndex, col, o);
    case 'techno':    return drawTechnoPart(g, partIndex, col, o);
    case 'elemental': return drawElementalPart(g, partIndex, col, o);
    case 'dominion':
    default:          return drawDominionPart(g, partIndex, col, o);
  }
}

// ⚡ 星算門 — 觀星水晶塔
function drawCelestialPart(g: Phaser.GameObjects.Graphics, i: number, col: Col, o: ShapeOpts): void {
  if (i === 0) { // 核心:塔身(細高 + 窗)
    panel(g, -34, -54, 68, 116, 12, col);
    g.fillStyle(o.bought ? o.pal.glow : col.stroke, o.bought ? 0.9 : 0.4);
    for (let k = 0; k < 3; k++) g.fillCircle(0, -28 + k * 28, 6);
  } else if (i === 1) { // 頂:天文台圓頂 + 望遠鏡
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillEllipse(0, 6, 84, 60);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeEllipse(0, 6, 84, 60);
    g.fillStyle(col.hi, col.fillAlpha * 0.6);
    g.fillEllipse(0, -4, 60, 30);
    g.fillStyle(o.bought ? o.pal.light : col.stroke, o.bought ? 1 : 0.5);
    g.fillRoundedRect(8, -34, 40, 12, 6);
  } else { // 左右:漂浮水晶(菱形)
    const dir = i === 2 ? -1 : 1;
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillTriangle(0, -34, -22, 0, 22, 0);
    g.fillTriangle(-22, 0, 22, 0, 0, 40);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeTriangle(0, -34, -22, 0, 22, 0);
    g.strokeTriangle(-22, 0, 22, 0, 0, 40);
    g.fillStyle(col.hi, col.fillAlpha * 0.7);
    g.fillTriangle(0, -34, dir * 4, 0, dir * -10, -2);
  }
}

// 🔮 符文門 — 符文神殿 / 盾牆
function drawRunicPart(g: Phaser.GameObjects.Graphics, i: number, col: Col, o: ShapeOpts): void {
  if (i === 0) { // 核心:神殿(寬體 + 柱 + 山形頂)
    panel(g, -52, -46, 104, 100, 12, col);
    g.fillStyle(col.hi, col.fillAlpha * 0.55);
    for (let k = -1; k <= 1; k++) g.fillRoundedRect(k * 30 - 6, -30, 12, 70, 4);
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillTriangle(-58, -44, 58, -44, 0, -78);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeTriangle(-58, -44, 58, -44, 0, -78);
  } else if (i === 1) { // 頂:符文核心(六邊形 + 符文)
    drawHex(g, 0, 0, 38, col);
    g.fillStyle(o.bought ? o.pal.glow : col.stroke, o.bought ? 0.95 : 0.45);
    g.fillRect(-3, -18, 6, 36);
    g.fillRect(-14, -3, 28, 6);
  } else { // 左右:盾牆(盾形)
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillRoundedRect(-26, -34, 52, 44, { tl: 14, tr: 14, bl: 0, br: 0 });
    g.fillTriangle(-26, 8, 26, 8, 0, 44);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeRoundedRect(-26, -34, 52, 44, { tl: 14, tr: 14, bl: 0, br: 0 });
    g.lineBetween(-26, 8, 0, 44);
    g.lineBetween(26, 8, 0, 44);
    g.fillStyle(col.hi, col.fillAlpha * 0.6);
    g.fillRect(-4, -22, 8, 40);
  }
}

// 🤖 機械門 — 機械齒輪廠
function drawTechnoPart(g: Phaser.GameObjects.Graphics, i: number, col: Col, o: ShapeOpts): void {
  if (i === 0) { // 核心:車庫(寬體 + 捲門 + 屋頂)
    panel(g, -56, -40, 112, 92, 10, col);
    g.lineStyle(2, col.stroke, col.strokeAlpha * 0.6);
    for (let k = -24; k <= 36; k += 14) g.lineBetween(-44, k, 44, k);
    g.fillStyle(col.hi, col.fillAlpha * 0.5);
    g.fillRoundedRect(-56, -48, 112, 14, { tl: 8, tr: 8, bl: 0, br: 0 });
  } else if (i === 1) { // 右上:大齒輪
    drawGear(g, 0, 0, 32, 10, col, o);
  } else if (i === 2) { // 左:推進器(噴口)
    panel(g, -22, -28, 44, 56, 8, col);
    g.fillStyle(o.bought ? o.pal.glow : col.stroke, o.bought ? 0.9 : 0.4);
    g.fillTriangle(-14, 28, 14, 28, 0, 46);
  } else { // 左上:雷達天線(碟 + 桿)
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillEllipse(0, -2, 56, 30);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeEllipse(0, -2, 56, 30);
    g.fillStyle(col.hi, col.fillAlpha * 0.6);
    g.fillCircle(0, -2, 6);
    g.lineStyle(3, col.stroke, col.strokeAlpha);
    g.lineBetween(0, -2, 0, 26);
  }
}

// 🌀 自然門 — 生態樹屋圓頂
function drawElementalPart(g: Phaser.GameObjects.Graphics, i: number, col: Col, o: ShapeOpts): void {
  if (i === 0) { // 核心:巨樹(樹幹 + 三層樹冠)
    g.fillStyle(lerpColor(col.fill, 0x4a2f12, 0.4), col.fillAlpha);
    g.fillRoundedRect(-16, -10, 32, 70, 8);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeRoundedRect(-16, -10, 32, 70, 8);
    g.fillStyle(col.fill, col.fillAlpha);
    g.fillCircle(0, -36, 44);
    g.fillCircle(-30, -16, 28);
    g.fillCircle(30, -16, 28);
    g.fillStyle(col.hi, col.fillAlpha * 0.5);
    g.fillCircle(-10, -44, 18);
  } else if (i === 1) { // 頂:生態圓頂(玻璃穹頂 + 格線)
    g.fillStyle(col.fill, col.fillAlpha * 0.85);
    g.fillEllipse(0, 10, 96, 72);
    g.lineStyle(2.5, col.stroke, col.strokeAlpha);
    g.strokeEllipse(0, 10, 96, 72);
    g.lineStyle(1.5, col.stroke, col.strokeAlpha * 0.6);
    g.lineBetween(0, -26, 0, 36);
    g.lineBetween(-46, 10, 46, 10);
    g.fillStyle(col.hi, col.fillAlpha * 0.5);
    g.fillEllipse(-18, -6, 30, 20);
  } else { // 左右:花叢(花瓣圓 + 莖)
    g.fillStyle(lerpColor(col.fill, 0x2f6b35, 0.4), col.fillAlpha);
    g.fillRoundedRect(-3, 0, 6, 36, 3);
    g.fillStyle(col.fill, col.fillAlpha);
    for (let k = 0; k < 5; k++) {
      const a = (k / 5) * Math.PI * 2;
      g.fillCircle(Math.cos(a) * 16, Math.sin(a) * 16 - 6, 11);
    }
    g.fillStyle(o.bought ? o.pal.light : col.stroke, o.bought ? 1 : 0.5);
    g.fillCircle(0, -6, 10);
  }
}

// 🏰 疆域門 — 黃金要塞
function drawDominionPart(g: Phaser.GameObjects.Graphics, i: number, col: Col, o: ShapeOpts): void {
  if (i === 0) { // 核心:主堡(寬牆 + 城垛 + 拱門)
    panel(g, -58, -40, 116, 96, 8, col);
    g.fillStyle(col.fill, col.fillAlpha);
    for (let x = -58; x < 58; x += 24) g.fillRect(x, -52, 14, 14);
    g.fillStyle(lerpColor(col.fill, 0x000000, 0.5), col.fillAlpha);
    g.fillRoundedRect(-16, 14, 32, 42, { tl: 16, tr: 16, bl: 0, br: 0 });
    g.fillStyle(o.bought ? o.pal.glow : col.stroke, o.bought ? 0.5 : 0.3);
    g.fillRect(-2, 20, 4, 30);
  } else if (i === 1) { // 頂:旗塔(尖塔 + 旗)
    panel(g, -22, -30, 44, 70, 6, col);
    g.fillStyle(col.fill, col.fillAlpha);
    for (let x = -22; x < 22; x += 14) g.fillRect(x, -40, 8, 12);
    g.fillStyle(o.bought ? 0xffd54f : col.stroke, o.bought ? 1 : 0.5);
    g.fillRect(0, -64, 2, 28);
    g.fillTriangle(2, -62, 2, -48, 24, -55);
  } else { // 左右:角樓(圓塔 + 錐頂 + 窗)
    panel(g, -22, -24, 44, 64, 8, col);
    g.fillStyle(o.bought ? lerpColor(o.pal.primary, o.pal.light, 0.5) : col.stroke, o.bought ? 1 : 0.5);
    g.fillTriangle(-26, -22, 26, -22, 0, -52);
    g.fillStyle(lerpColor(col.fill, 0x000000, 0.5), col.fillAlpha);
    g.fillCircle(0, -2, 7);
  }
}

/** 六邊形(符文核心用)。 */
function drawHex(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, col: Col): void {
  const pts: Phaser.Geom.Point[] = [];
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 3) * k - Math.PI / 2;
    pts.push(new Phaser.Geom.Point(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
  }
  g.fillStyle(col.fill, col.fillAlpha);
  g.fillPoints(pts, true);
  g.lineStyle(2.5, col.stroke, col.strokeAlpha);
  g.strokePoints(pts, true);
}

/** 齒輪(機械大齒輪用):圓盤 + 齒 + 軸心孔。 */
function drawGear(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, teeth: number, col: Col, o: ShapeOpts): void {
  g.fillStyle(col.fill, col.fillAlpha);
  for (let k = 0; k < teeth; k++) {
    const a = (Math.PI * 2 / teeth) * k;
    const tx = cx + Math.cos(a) * (r + 7);
    const ty = cy + Math.sin(a) * (r + 7);
    g.fillRect(tx - 5, ty - 5, 10, 10);
  }
  g.fillCircle(cx, cy, r);
  g.lineStyle(2.5, col.stroke, col.strokeAlpha);
  g.strokeCircle(cx, cy, r);
  g.fillStyle(lerpColor(col.fill, 0x000000, 0.5), col.fillAlpha);
  g.fillCircle(cx, cy, r * 0.34);
  g.fillStyle(o.bought ? o.pal.glow : col.stroke, o.bought ? 0.8 : 0.4);
  g.fillCircle(cx, cy, r * 0.18);
}
